import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Language, HabitTemplate, HabitCompletion, HabitCustomization, UserSettings, UserProgress, Habit } from '@/types';

const DAY_MAP: Record<number, string> = {
  0: 'zo', 1: 'ma', 2: 'di', 3: 'wo', 4: 'do', 5: 'vr', 6: 'za',
};

// Auto-detect browser language for first-time users
function detectBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return 'nl';

  const browserLang = navigator.language || '';
  const langCode = browserLang.toLowerCase().split('-')[0];

  if (langCode === 'fr') return 'fr';
  if (langCode === 'en') return 'en';
  return 'nl'; // Default to Dutch for Belgian users
}

interface AppState {
  // Settings
  settings: UserSettings;
  setLanguage: (lang: Language) => void;
  setNotifications: (enabled: boolean) => void;
  setReminderTime: (time: string) => void;
  setOnboardingComplete: (complete: boolean) => void;

  // Progress
  progress: UserProgress;
  addTemplate: (templateId: string) => void;
  removeTemplate: (templateId: string) => void;
  setSelectedTemplate: (templateId: string, startDate: string) => void; // Keep for backward compat
  toggleHabitCompletion: (habitId: string, date: string, timeSpentMinutes?: number) => void;
  startHabitProgress: (habitId: string, date: string) => void;
  skipHabit: (habitId: string, date: string) => void;
  updateHabitCustomization: (habitId: string, customization: HabitCustomization) => void;
  resetProgress: () => void;

  // Templates (loaded from JSON)
  templates: HabitTemplate[];
  setTemplates: (templates: HabitTemplate[]) => void;

  // Current templates helper
  getSelectedTemplates: () => HabitTemplate[];
}

const defaultSettings: UserSettings = {
  language: detectBrowserLanguage(),
  notifications: false,
  reminderTime: '09:00',
  onboardingComplete: false,
};

const defaultProgress: UserProgress = {
  selectedTemplates: [],
  startDate: null,
  completions: [],
  streak: 0,
  lastCompletedDate: null,
  customHabits: {},
};

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Settings
      settings: defaultSettings,
      setLanguage: (lang) =>
        set((state) => ({ settings: { ...state.settings, language: lang } })),
      setNotifications: (enabled) =>
        set((state) => ({ settings: { ...state.settings, notifications: enabled } })),
      setReminderTime: (time) =>
        set((state) => ({ settings: { ...state.settings, reminderTime: time } })),
      setOnboardingComplete: (complete) =>
        set((state) => ({ settings: { ...state.settings, onboardingComplete: complete } })),

      // Progress
      progress: defaultProgress,
      addTemplate: (templateId) =>
        set((state) => {
          if ((state.progress.selectedTemplates || []).includes(templateId)) {
            return state; // Already added
          }
          const today = new Date().toISOString().split('T')[0];
          return {
            progress: {
              ...state.progress,
              // Add new templates at the top
              selectedTemplates: [templateId, ...(state.progress.selectedTemplates || [])],
              startDate: state.progress.startDate || today,
            },
          };
        }),
      removeTemplate: (templateId) =>
        set((state) => ({
          progress: {
            ...state.progress,
            selectedTemplates: (state.progress.selectedTemplates || []).filter((id) => id !== templateId),
          },
        })),
      setSelectedTemplate: (templateId, startDate) =>
        set((state) => ({
          progress: {
            ...state.progress,
            selectedTemplates: [templateId],
            startDate,
            completions: [],
            streak: 0,
            lastCompletedDate: null,
          },
        })),
      toggleHabitCompletion: (habitId, date, timeSpentMinutes) =>
        set((state) => {
          const existing = state.progress.completions.find(
            (c) => c.habitId === habitId && c.date === date
          );

          let newCompletions: HabitCompletion[];
          if (existing) {
            // Toggle existing
            newCompletions = state.progress.completions.map((c) =>
              c.habitId === habitId && c.date === date
                ? { ...c, completed: !c.completed, timeSpentMinutes: !c.completed ? timeSpentMinutes : undefined }
                : c
            );
          } else {
            // Add new
            newCompletions = [
              ...state.progress.completions,
              { habitId, date, completed: true, timeSpentMinutes },
            ];
          }

          // Get all recurring habits from selected templates
          const selectedTemplates = state.templates.filter(
            (t) => (state.progress.selectedTemplates || []).includes(t.id)
          );
          const allHabits = selectedTemplates.flatMap((t) => t.habits);

          // Calculate streak with full context
          const today = new Date().toISOString().split('T')[0];
          const { streak, lastCompletedDate } = calculateStreak(
            newCompletions,
            today,
            allHabits,
            state.progress.customHabits || {},
            state.progress.startDate
          );

          return {
            progress: {
              ...state.progress,
              completions: newCompletions,
              streak,
              lastCompletedDate,
            },
          };
        }),
      startHabitProgress: (habitId, date) =>
        set((state) => {
          const currentBusy = state.progress.busyHabits || {};
          const habitBusy = currentBusy[habitId] || [];

          // Toggle busy - if already busy, remove it
          const isAlreadyBusy = habitBusy.includes(date);
          const newHabitBusy = isAlreadyBusy
            ? habitBusy.filter((d) => d !== date)
            : [...habitBusy, date];

          return {
            progress: {
              ...state.progress,
              busyHabits: {
                ...currentBusy,
                [habitId]: newHabitBusy,
              },
            },
          };
        }),
      skipHabit: (habitId, date) =>
        set((state) => {
          const currentSkipped = state.progress.skippedHabits || {};
          const habitSkipped = currentSkipped[habitId] || [];

          // Toggle skip - if already skipped, remove it
          const isAlreadySkipped = habitSkipped.includes(date);
          const newHabitSkipped = isAlreadySkipped
            ? habitSkipped.filter((d) => d !== date)
            : [...habitSkipped, date];

          return {
            progress: {
              ...state.progress,
              skippedHabits: {
                ...currentSkipped,
                [habitId]: newHabitSkipped,
              },
            },
          };
        }),
      updateHabitCustomization: (habitId, customization) =>
        set((state) => {
          const currentCustomHabits = state.progress.customHabits || {};
          const existing = currentCustomHabits[habitId] || {};

          // Merge customization, but explicitly handle undefined values
          // undefined means "use default" so we should remove the key
          const updated: HabitCustomization = {};

          // Copy existing values
          if (existing.disabled !== undefined) updated.disabled = existing.disabled;
          if (existing.days !== undefined) updated.days = existing.days;
          if (existing.targetTime !== undefined) updated.targetTime = existing.targetTime;
          if (existing.duration !== undefined) updated.duration = existing.duration;
          if (existing.oneTime !== undefined) updated.oneTime = existing.oneTime;
          if (existing.reminder !== undefined) updated.reminder = existing.reminder;
          if (existing.reminderTime !== undefined) updated.reminderTime = existing.reminderTime;

          // Apply new customization (undefined removes, defined value sets)
          if ('disabled' in customization) {
            if (customization.disabled !== undefined) updated.disabled = customization.disabled;
            else delete updated.disabled;
          }
          if ('days' in customization) {
            if (customization.days !== undefined) updated.days = customization.days;
            else delete updated.days;
          }
          if ('targetTime' in customization) {
            if (customization.targetTime !== undefined) updated.targetTime = customization.targetTime;
            else delete updated.targetTime;
          }
          if ('duration' in customization) {
            if (customization.duration !== undefined) updated.duration = customization.duration;
            else delete updated.duration;
          }
          if ('oneTime' in customization) {
            if (customization.oneTime !== undefined) updated.oneTime = customization.oneTime;
            else delete updated.oneTime;
          }
          if ('reminder' in customization) {
            if (customization.reminder !== undefined) updated.reminder = customization.reminder;
            else delete updated.reminder;
          }
          if ('reminderTime' in customization) {
            if (customization.reminderTime !== undefined) updated.reminderTime = customization.reminderTime;
            else delete updated.reminderTime;
          }

          return {
            progress: {
              ...state.progress,
              customHabits: {
                ...currentCustomHabits,
                [habitId]: updated,
              },
            },
          };
        }),
      resetProgress: () =>
        set((state) => ({
          progress: defaultProgress,
          settings: { ...state.settings, onboardingComplete: false },
        })),

      // Templates
      templates: [],
      setTemplates: (templates) => set({ templates }),

      // Helper
      getSelectedTemplates: () => {
        const state = get();
        return state.templates.filter((t) => (state.progress.selectedTemplates || []).includes(t.id));
      },
    }),
    {
      name: 'easyhabits-storage',
      partialize: (state) => ({
        settings: state.settings,
        progress: state.progress,
      }),
    }
  )
);

function calculateStreak(
  completions: HabitCompletion[],
  today: string,
  allHabits: Habit[],
  customHabits: Record<string, HabitCustomization>,
  startDate: string | null
): { streak: number; lastCompletedDate: string | null } {
  if (!startDate || allHabits.length === 0) {
    return { streak: 0, lastCompletedDate: null };
  }

  // Helper: get recurring habits scheduled for a specific date
  const getScheduledRecurringHabits = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    const dayCode = DAY_MAP[date.getDay()];
    const defaultDays = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

    return allHabits.filter((habit) => {
      const custom = customHabits[habit.id];

      // Skip one-time habits
      const isOneTime = custom?.oneTime ?? habit.oneTime ?? false;
      if (isOneTime) return false;

      // Skip disabled habits
      if (custom?.disabled) return false;

      // Check if scheduled for this day
      const effectiveDays = custom?.days || habit.days || defaultDays;
      return effectiveDays.includes(dayCode);
    });
  };

  // Helper: check if all scheduled recurring habits are completed for a date
  const isDayFullyCompleted = (dateStr: string) => {
    const scheduled = getScheduledRecurringHabits(dateStr);
    if (scheduled.length === 0) return false; // No habits scheduled = doesn't count

    const completedHabitIds = new Set(
      completions
        .filter((c) => c.date === dateStr && c.completed)
        .map((c) => c.habitId)
    );

    return scheduled.every((habit) => completedHabitIds.has(habit.id));
  };

  // Find last completed date
  const completedDates = [...new Set(completions.filter((c) => c.completed).map((c) => c.date))].sort().reverse();
  const lastCompletedDate = completedDates[0] || null;

  // Calculate streak
  let streak = 0;
  const todayDate = new Date(today + 'T12:00:00');
  const startDateObj = new Date(startDate + 'T12:00:00');
  let checkDate = new Date(todayDate);

  // If today is not fully completed, start checking from yesterday
  if (!isDayFullyCompleted(today)) {
    checkDate.setDate(checkDate.getDate() - 1);
  }

  // Count consecutive days going backwards
  for (let i = 0; i < 365; i++) {
    const dateStr = checkDate.toISOString().split('T')[0];

    // Don't count before start date
    if (checkDate < startDateObj) break;

    const scheduled = getScheduledRecurringHabits(dateStr);

    // Skip days with no scheduled habits (don't break streak)
    if (scheduled.length === 0) {
      checkDate.setDate(checkDate.getDate() - 1);
      continue;
    }

    if (isDayFullyCompleted(dateStr)) {
      streak++;
      checkDate.setDate(checkDate.getDate() - 1);
    } else {
      break;
    }
  }

  return { streak, lastCompletedDate };
}
